/**
 * Header Help menu (#776): replay the first-run tour, the "how prediction contracts work" page (#777), the privacy
 * policy (#727) and the language switcher (#799: system default + every locale by its own name, remembered per device).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LOCALES } from '../i18n/keys';
import { localeDir, setLocaleChoice, type LocaleChoice, type LocaleCode } from '../i18n/i18n';
import { useLocale, useT } from '../i18n/useT';
import { useTour } from './context';
import { LEARN_PATH } from './steps';
import { useMenu } from '../components/useMenu';

const ITEM = 'block w-full rounded-md px-3 py-2 text-start text-sm hover:bg-surface-2';
const RADIO = 'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-start text-sm hover:bg-surface-2';

/** The pseudo-locale is a testing tool: listed only in dev builds or while it is active (?lang=en-XA). */
function languageOptions(active: string) {
  return LOCALES.filter((l) => !l.pseudo || import.meta.env.DEV || active === l.code);
}

export default function HelpMenu() {
  const { replay } = useTour();
  const t = useT();
  const { locale, choice } = useLocale();
  const { open, setOpen, ref } = useMenu();
  const [langOpen, setLangOpen] = useState(false);

  // The language list starts folded each time the menu opens.
  useEffect(() => {
    if (!open) setLangOpen(false);
  }, [open]);

  return (
    <div ref={ref} className="relative" data-tour="help">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('tour.ui.help_menu')}
        title={t('tour.ui.help_menu')}
        onClick={() => setOpen((o) => !o)}
        className="flex h-7 w-7 items-center justify-center rounded-full border border-border text-sm font-semibold text-muted hover:bg-surface-2 hover:text-fg"
      >
        ?
      </button>
      {open && (
        <div role="menu" aria-label={t('tour.ui.help_menu')} className="card absolute end-0 z-30 mt-2 w-64 p-1 shadow-xl">
          <button
            type="button"
            role="menuitem"
            className={ITEM}
            onClick={() => {
              setOpen(false);
              replay();
            }}
          >
            {t('tour.ui.replay')}
          </button>
          <Link role="menuitem" to={LEARN_PATH} className={ITEM} onClick={() => setOpen(false)}>
            {t('tour.ui.learn')}
          </Link>
          <Link role="menuitem" to="/privacy" className={ITEM} onClick={() => setOpen(false)}>
            {t('privacy.title')}
          </Link>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            role="menuitem"
            aria-haspopup="true"
            aria-expanded={langOpen}
            aria-label={t('lang.current', { name: nativeName(locale) })}
            className={`${ITEM} flex items-center justify-between gap-2`}
            onClick={() => setLangOpen((o) => !o)}
            data-testid="lang-menu"
          >
            <span>{t('lang.menu')}</span>
            <span className="truncate text-xs text-muted">
              <bdi translate="no" lang={locale} dir={localeDir(locale)}>
                {nativeName(locale)}
              </bdi>{' '}
              {langOpen ? '▴' : '▾'}
            </span>
          </button>
          {langOpen && (
            <div role="group" aria-label={t('lang.list')} className="max-h-72 overflow-y-auto" data-testid="lang-list">
              {(['system', ...languageOptions(locale).map((l) => l.code)] as LocaleChoice[]).map((c) => (
                <button
                  key={c}
                  type="button"
                  role="menuitemradio"
                  aria-checked={choice === c}
                  className={RADIO}
                  data-testid={`lang-${c}`}
                  onClick={() => {
                    setOpen(false);
                    void setLocaleChoice(c);
                  }}
                >
                  <span aria-hidden className="w-3 text-accent">
                    {choice === c ? '✓' : ''}
                  </span>
                  {c === 'system' ? (
                    <span>{t('lang.system')}</span>
                  ) : (
                    <span translate="no" lang={c} dir={localeDir(c as LocaleCode)}>
                      {nativeName(c)}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function nativeName(code: string): string {
  return LOCALES.find((l) => l.code === code)?.nativeName ?? code;
}
